import { useEffect, useRef, useState } from 'react'
import { Icon } from '../common/Icon'
import { cn } from '../../lib/cn'

interface Option {
  value: string
  label: string
}

interface SiteSelectProps {
  id: string
  value: string
  onChange: (value: string) => void
  options: Option[]
  placeholder?: string
  error?: boolean
  ariaDescribedBy?: string
}

/**
 * Premium styled select for the public site. Replaces the native <select> (whose OS-drawn option list
 * can't be themed) with a button trigger + a themed listbox popover: rounded, soft-shadowed, brand-tinted
 * selection, keyboard-navigable, closes on outside-click / Escape.
 */
export function SiteSelect({ id, value, onChange, options, placeholder = 'Select…', error, ariaDescribedBy }: SiteSelectProps) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const selected = options.find((o) => o.value === value)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // On open, focus the currently-selected option for keyboard nav.
  useEffect(() => {
    if (!open) return
    const idx = options.findIndex((o) => o.value === value)
    setActive(idx >= 0 ? idx : 0)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (v: string) => {
    onChange(v)
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) return setOpen(true)
      setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length)
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (!open) setOpen(true)
      else if (options[active]) commit(options[active].value)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-invalid={error || undefined}
        aria-describedby={ariaDescribedBy}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-4 py-3 text-left text-[0.95rem] outline-none transition-colors focus:ring-2 focus:ring-lime-bright',
          error ? 'border-red-400' : 'border-sand-200 focus:border-brand-400',
          selected ? 'text-ink' : 'text-ink-300',
        )}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <Icon name="ChevronDown" className={cn('h-4 w-4 shrink-0 text-ink-400 transition-transform duration-200', open && 'rotate-180')} />
      </button>

      {open && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          className="absolute z-40 mt-2 max-h-64 w-full animate-fade-in overflow-auto rounded-xl border border-sand-200 bg-white p-1.5 shadow-pop"
        >
          {options.map((o, i) => {
            const isSel = o.value === value
            const isActive = i === active
            return (
              <li
                key={o.value}
                id={`${id}-opt-${i}`}
                role="option"
                aria-selected={isSel}
                onMouseEnter={() => setActive(i)}
                onClick={() => commit(o.value)}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3.5 py-2.5 text-[0.95rem] transition-colors',
                  isActive ? 'bg-sand-100' : '',
                  isSel ? 'font-semibold text-brand-700' : 'text-ink-700',
                )}
              >
                <span className="truncate">{o.label}</span>
                {isSel && <Icon name="Check" className="h-4 w-4 shrink-0 text-brand-600" />}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
